'use client';

import { Globe, Link2, Lock, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

export type Visibility = 'private' | 'unlisted' | 'public';
export type VisibilitySource = 'self' | 'folder' | 'workspace';

interface VisibilityBadgeProps {
  visibility: Visibility;
  source?: VisibilitySource;
  sourceName?: string | null;
  /** Non-zero when the content has explicit user grants (e.g. shared with 3 people). */
  grantCount?: number;
  size?: 'xs' | 'sm';
  className?: string;
}

const META: Record<
  Visibility,
  { label: string; icon: typeof Lock; chipClass: string }
> = {
  private: {
    label: 'Private',
    icon: Lock,
    chipClass:
      'bg-zinc-100 text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300',
  },
  unlisted: {
    label: 'Unlisted',
    icon: Link2,
    chipClass:
      'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300',
  },
  public: {
    label: 'Public',
    icon: Globe,
    chipClass:
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  },
};

export function VisibilityBadge({
  visibility,
  source = 'self',
  sourceName,
  grantCount,
  size = 'xs',
  className,
}: VisibilityBadgeProps) {
  const meta = META[visibility];
  const Icon = meta.icon;
  const dims = size === 'xs' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const text = size === 'xs' ? 'text-[10px]' : 'text-[11px]';
  const inherited = source !== 'self';
  const title = inherited
    ? `${meta.label} (inherited from ${source}${sourceName ? `: ${sourceName}` : ''})`
    : meta.label;

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium uppercase tracking-wide',
          meta.chipClass,
          text,
          inherited && 'ring-1 ring-inset ring-amber-400/50',
        )}
        title={title}
        data-testid="visibility-badge"
        data-visibility={visibility}
        data-source={source}
      >
        <Icon className={dims} aria-hidden />
        <span>{meta.label}</span>
      </span>
      {grantCount && grantCount > 0 ? (
        <span
          className={cn(
            'inline-flex items-center gap-0.5 rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary',
            text,
          )}
          title={`Shared with ${grantCount} ${grantCount === 1 ? 'person' : 'people'}`}
          data-testid="visibility-grant-count"
        >
          <Users className={dims} aria-hidden />
          <span>{grantCount}</span>
        </span>
      ) : null}
    </span>
  );
}
